import { z } from 'zod'

/**
 * The SKU drawer's place in a page's URL, so an open drawer is linkable:
 * `sku=<store>.<sku>.<currency>`, the tab and its page. Every field optional
 * and caught, as a query string is user input.
 */
export const skuDocumentsSearchFields = {
  sku: z
    .string()
    .regex(/^\d+\.\d+\.[A-Z]{3}$/)
    .optional()
    .catch(undefined)
    .describe(
      'The SKU whose documents drawer is open, as storeId.skuId.CURRENCY (e.g. 12.4471.EUR).',
    ),
  skuTab: z
    .enum(['invoices'])
    .optional()
    .catch(undefined)
    .describe('invoices opens the drawer’s Invoice lines tab; absent is Orders.'),
  skuPage: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the drawer’s list; default 1.'),
}

export type SkuDocumentsSearch = {
  sku?: string
  skuTab?: 'invoices'
  skuPage?: number
}

export type SkuRef = { storeId: number; skuId: number; currency: string }

export const SKU_DOCUMENTS_CLOSED: SkuDocumentsSearch = {
  sku: undefined,
  skuTab: undefined,
  skuPage: undefined,
}

/** The row's key in the URL. */
export function skuKey(ref: SkuRef): string {
  return `${ref.storeId}.${ref.skuId}.${ref.currency}`
}

/** The row a URL key names, or nothing. */
export function parseSkuKey(key: string | undefined): SkuRef | undefined {
  if (!key) return undefined
  const [store, sku, currency] = key.split('.')
  return { storeId: Number(store), skuId: Number(sku), currency: currency! }
}

/** The search that opens the drawer on a row: its orders, first page. */
export function openSkuDocuments(row: {
  store_id?: number | null
  sku_id?: number | null
  currency_code: string
}): SkuDocumentsSearch | undefined {
  if (!row.store_id || !row.sku_id) return undefined
  return {
    sku: skuKey({ storeId: row.store_id, skuId: row.sku_id, currency: row.currency_code }),
    skuTab: undefined,
    skuPage: undefined,
  }
}
